/**
 * handoverLive — lay the app's own station scans over the Production Grid's WIPs,
 * for the Fulfillment Handover only.
 *
 * WHY (Mikko, 2026-10-12): the handover read `iSyn_WIPs` as the last workbook
 * import left it. The import stopped on 2026-09-08 and stations went native, so
 * everything scanned since — a month of BQC2 to BQC5 — lived only in the scan
 * lists (cut-ready, dispatch slips, receiving, QC) and never reached their ERP.
 * The Production Grid screen already tops itself up from those lists at read time
 * (production-grid/liveStageTopUp.ts); the handover did not.
 *
 * THE RULE IS "FILL BLANKS, NEVER OVERRIDE". A field the import already carries is
 * left exactly as it is: the import and the scan lists describe the same event, so
 * taking the scan's figure over the import's would double or move it. A blank field
 * is the only thing a scan may supply — the same safety property the live top-up
 * names ("blank means the workbook has not carried this bundle yet").
 *
 * PURE and read-only: it returns new objects and writes nothing, not even to
 * `iSyn_WIPs`, which the Grid import rewrites in full.
 */
import type { CurrentStage, IProductionWip } from "../../lib/sp/ProductionGridService";
import type { ICutReadyScan } from "../../lib/sp/WorkcentersService";
import type { ISlip } from "../../lib/sp/DispatchService";
import type { IReceivingScan } from "./receivingLedger";
import type { IQcScan } from "./qcLedger";

export interface ILiveStationRecords {
  cut: readonly ICutReadyScan[];
  slips: readonly ISlip[];
  receiving: readonly IReceivingScan[];
  qc: readonly IQcScan[];
}

const STAGE_ORDER: Record<string, number> = { BQC1: 1, BQC2: 2, BQC3: 3, BQC4: 4, BQC5: 5, DONE: 6 };

/** The same WIP number however it was typed, as every other comparison here does. */
function key(id: string | undefined): string {
  return (id ?? "").trim().toUpperCase();
}

function blank(v: string | undefined | null): boolean {
  return !v || v.trim() === "";
}

function laterIso(a: string | undefined, b: string): string {
  return !a || b > a ? b : a;
}

interface IByWip {
  cutAt?: string; cutter?: string;
  slip?: ISlip;
  recvQty: number; recvAt?: string;
  passQty: number; passAt?: string; passBy?: string;
  repairQty: number; repairAt?: string; repairBy?: string;
}

function index(rec: ILiveStationRecords): Map<string, IByWip> {
  const out = new Map<string, IByWip>();
  const at = (id: string): IByWip => {
    let r = out.get(id);
    if (!r) { r = { recvQty: 0, passQty: 0, repairQty: 0 }; out.set(id, r); }
    return r;
  };

  for (const c of rec.cut) {
    if (c.undoneAt || !c.scannedAt) continue;
    const r = at(key(c.wipId));
    // A bundle is cut once: the EARLIEST live scan is the cutting day.
    if (!r.cutAt || c.scannedAt < r.cutAt) { r.cutAt = c.scannedAt; r.cutter = c.cutter; }
  }

  // The FIRST issued slip naming a WIP — the rule every other reader uses
  // (qcIndex.slipFor), so a re-dispatch after repair cannot move its DS number.
  const slips = [...rec.slips]
    .filter((s) => s.slipStatus === "Issued")
    .sort((a, b) => (a.generatedAt || "").localeCompare(b.generatedAt || ""));
  for (const s of slips) {
    for (const id of s.wipIds) {
      const r = at(key(id));
      if (!r.slip) r.slip = s;
    }
  }

  for (const v of rec.receiving) {
    if (v.undoneAt || !v.receivedAt || !(v.qty > 0)) continue;
    const r = at(key(v.wipId));
    r.recvQty += v.qty;
    r.recvAt = laterIso(r.recvAt, v.receivedAt);
  }

  for (const q of rec.qc) {
    if (q.undoneAt || !q.qcAt || !(q.qty > 0)) continue;
    const r = at(key(q.wipId));
    if (q.outcome === "pass") {
      r.passQty += q.qty;
      if (!r.passAt || q.qcAt >= r.passAt) { r.passAt = q.qcAt; r.passBy = q.qcBy; }
    } else {
      r.repairQty += q.qty;
      if (!r.repairAt || q.qcAt >= r.repairAt) { r.repairAt = q.qcAt; r.repairBy = q.qcBy; }
    }
  }
  return out;
}

function advance(current: CurrentStage | undefined, to: CurrentStage): CurrentStage {
  return (STAGE_ORDER[current ?? ""] ?? 0) >= (STAGE_ORDER[to] ?? 0) ? (current as CurrentStage) : to;
}

/** What the overlay changed, for the screen to say out loud. */
export interface ILiveOverlayTally {
  cut: number; assigned: number; received: number; passed: number; failed: number;
  /** WIPs a live scan names that the Grid's list does not hold at all. */
  notInGrid: number;
  /** WIPs moved up a stage by a live scan though every date was already filled. */
  advanced: number;
}

export function overlayLiveStations(
  wips: readonly IProductionWip[],
  rec: ILiveStationRecords,
): { wips: IProductionWip[]; tally: ILiveOverlayTally } {
  const byWip = index(rec);
  const tally: ILiveOverlayTally = { cut: 0, assigned: 0, received: 0, passed: 0, failed: 0, notInGrid: 0, advanced: 0 };
  if (byWip.size === 0) return { wips: [...wips], tally };

  const inGrid = new Set(wips.map((w) => key(w.wipId)));
  for (const id of byWip.keys()) if (id && !inGrid.has(id)) tally.notInGrid += 1;

  const out = wips.map((w) => {
    // A cancelled, lost or damaged bundle never goes to the ERP, whatever a scan says.
    if (w.cancellationStatus === "cancelled" || w.currentStage === "LOST" || w.currentStage === "DAMAGED") return w;
    const r = byWip.get(key(w.wipId));
    if (!r) return w;

    let next: IProductionWip = w;
    const set = (patch: Partial<IProductionWip>) => { next = { ...next, ...patch }; };

    // A live scan PROVES the stage, whatever the import's date fields say. The
    // cutting table is gated on the stage, and a bundle whose cutting date was
    // already filled at BQC1 (the day it was assigned to the cutter) never reached
    // the blank-only branches below, so it stayed at BQC1 and got no cutting row.
    const proven: CurrentStage | undefined =
      r.passQty > 0 || r.repairQty > 0 ? "BQC5"
        : r.recvQty > 0 ? "BQC4"
        : r.slip ? "BQC3"
        : r.cutAt ? "BQC2"
        : undefined;
    if (proven && (STAGE_ORDER[w.currentStage ?? ""] ?? 0) < STAGE_ORDER[proven]) {
      set({ currentStage: proven });
      tally.advanced += 1;
    }

    // BQC2 — the cutting day and the people at the table.
    if (r.cutAt && blank(w.dateAssignedToCutter)) {
      set({
        dateAssignedToCutter: r.cutAt,
        cutOperator: blank(w.cutOperator) && !blank(r.cutter) ? r.cutter : w.cutOperator,
        currentStage: advance(next.currentStage, "BQC2"),
      });
      tally.cut += 1;
    }

    // BQC3 — the dispatch slip: DS number, sewer and day.
    if (r.slip && blank(w.assignedDS)) {
      set({
        assignedDS: r.slip.dsNumber,
        assignedSewer: blank(w.assignedSewer) ? r.slip.sewer : w.assignedSewer,
        dispatchedAt: blank(w.dispatchedAt) ? r.slip.generatedAt : w.dispatchedAt,
        currentStage: advance(next.currentStage, "BQC3"),
      });
      tally.assigned += 1;
    }

    // BQC4 — what came back, and when. Only where the import has no receipt at all.
    if (r.recvQty > 0 && blank(w.receivedAt) && !((w.receivedQty ?? 0) > 0)) {
      set({
        receivedAt: r.recvAt,
        receivedQty: r.recvQty,
        grn: blank(w.grn) && r.slip?.grn ? r.slip.grn : w.grn,
        currentStage: advance(next.currentStage, "BQC4"),
      });
      tally.received += 1;
    }

    // BQC5 — sets passed, and sets sent back for repair.
    if (r.passQty > 0 && blank(w.qcPassedAt) && !((w.qcPassedQty ?? 0) > 0)) {
      set({
        qcPassedAt: r.passAt,
        qcPassedQty: r.passQty,
        qcPersonnel: blank(w.qcPersonnel) ? r.passBy : w.qcPersonnel,
        currentStage: advance(next.currentStage, "BQC5"),
      });
      tally.passed += 1;
    }
    if (r.repairQty > 0 && !((w.qcRepairQty ?? 0) > 0)) {
      set({
        qcRepairQty: r.repairQty,
        // The failed table reads the QC date and person from the same two fields
        // as the passed one; only supply them when a pass has not.
        qcPassedAt: blank(next.qcPassedAt) ? r.repairAt : next.qcPassedAt,
        qcPersonnel: blank(next.qcPersonnel) ? r.repairBy : next.qcPersonnel,
        currentStage: advance(next.currentStage, "BQC5"),
      });
      tally.failed += 1;
    }
    return next;
  });
  return { wips: out, tally };
}
